import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import {
  CreateTaskRequest,
  Task,
  TasksQuery,
  UpdateAllTaskRequest,
  UpdateTaskRequest,
} from './Tasks.types';
import {
  createTask,
  deleteTask,
  getTask,
  listTasks,
  listTasksReduced,
  taskActivateOrInactivate,
  updateAllTask,
  updateTask,
} from './Tasks.service';
import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { useIntl } from 'react-intl';
import { message, notification } from 'antd';
import notificationError from '@/utils/functions/notificationError';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { queryClient } from '@/Root';

export function useTasks(
  params?: TasksQuery,
  enabled = true,
  refreshToken = false,
) {
  const cache = queryClient.getQueryData(['tasks', { ...params }]);

  return useInfiniteQuery(
    ['tasks', { ...params }],
    ({ pageParam }) => {
      const req: TasksQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listTasks(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      refetchOnWindowFocus: refreshToken,
      enabled: enabled && !cache,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useTasksReduced(params?: TasksQuery, enabled = true) {
  const cache = queryClient.getQueryData(['task/list/reduced', { ...params }]);

  return useInfiniteQuery(
    ['task/list/reduced', { ...params }],
    ({ pageParam }) => {
      const req: TasksQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listTasksReduced(req);
    },
    {
      enabled: enabled && !cache,
      getNextPageParam: (lastPage) => lastPage.nextToken,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useTask(id?: number, enabled = true) {
  return useQuery(['tasks', id], () => getTask(id), {
    onError(error) {
      notificationError(error);
    },
    enabled: enabled && !!id,
  });
}

export function useTaskMutation() {
  return useMutation({
    mutationFn: (id: number) => {
      const cache = queryClient.getQueryData<Task>(['tasks', id]);

      if (cache) return Promise.resolve(cache);

      return getTask(id);
    },
    onSuccess(data, id) {
      queryClient.setQueryData(['tasks', id], data);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useCreateTask() {
  const queryChanel = useQueryChanel();

  return useMutation((obj: CreateTaskRequest) => createTask(obj), {
    onSuccess() {
      queryChanel({
        resetQueries: [['tasks'], ['task/list/reduced']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useUpdateTask() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  return useMutation((obj: UpdateTaskRequest) => updateTask(obj), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'tasks.update.notification.success',
        }),
        description: intl.formatMessage({
          id: 'tasks.update.notification.successDescription',
        }),
      });

      queryChanel({
        invalidateQueries: [['workorder/kit/detail']],
        resetQueries: [['tasks'], ['task/list/reduced']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useUpdateAllTask() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  return useMutation((obj: UpdateAllTaskRequest) => updateAllTask(obj), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'tasks.update.notification.success',
        }),
        description: intl.formatMessage({
          id: 'tasks.update.notification.successDescriptionAll',
        }),
      });

      queryChanel({
        resetQueries: [['tasks'], ['task/list/reduced']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useDeleteTask() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  return useMutation((id: number) => deleteTask(id), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'tasks.remove.notification.success',
        }),
        description: intl.formatMessage({
          id: 'tasks.remove.notification.successDescription',
        }),
      });

      queryChanel({
        resetQueries: [['tasks'], ['task/list/reduced']],
      });
    },

    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('Task already has a link with another entity')
              ? 'Não é possivel excluir o serviço, pois ele já tem vínculo com outro módulo.'
              : err.includes('Task cannot be deleted due to relation.')
              ? 'Não é possivel excluir o serviço, pois ele já tem vínculo com outro módulo.'
              : err,
          ),
      });
    },
  });
}

export function useActiveOrInactivateTask() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  return useMutation((taskId: number) => taskActivateOrInactivate(taskId), {
    onSuccess() {
      message.success(
        intl.formatMessage({ id: 'task.status.message.success' }),
      );

      queryChanel({
        resetQueries: [['tasks'], ['task/list/reduced']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}
